import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis
} from "recharts";

// ─── Color System ─────────────────────────────────────────────────────────────
const C = {
  bg: "#080d14", surface: "#0e1520", card: "#121a27", border: "#1c2a3a",
  accent: "#0ea5e9", accent2: "#6366f1", accent3: "#10b981", accent4: "#f59e0b",
  accent5: "#ef4444", text: "#e2eaf4", muted: "#5a7292", dim: "#2a3d52",
};
const SSID_COLORS = ["#0ea5e9","#6366f1","#10b981","#f59e0b","#ef4444","#ec4899","#8b5cf6","#14b8a6"];
const BAND_COLORS = { ng:"#6366f1", na:"#0ea5e9", ax:"#10b981", "2.4GHz":"#6366f1", "5GHz":"#0ea5e9", "6GHz":"#10b981" };

const fmt = n => { if(n==null)return"—"; if(n>=1e9)return(n/1e9).toFixed(1)+" GB"; if(n>=1e6)return(n/1e6).toFixed(1)+" MB"; if(n>=1e3)return(n/1e3).toFixed(1)+" KB"; return n+" B"; };
const fmtNum = n => n?.toLocaleString()??"—";
const fmtSec = s => { if(!s)return"—"; if(s<60)return`${Math.round(s)}s`; if(s<3600)return`${Math.round(s/60)}m`; return`${(s/3600).toFixed(1)}h`; };

const healthScore = ap => {
  let score = 100;
  if(ap.avg_rssi!=null){ if(ap.avg_rssi< -80)score-=40; else if(ap.avg_rssi< -70)score-=25; else if(ap.avg_rssi< -60)score-=10; }
  else score-=20;
  if(ap.clients===0)score-=20;
  if(ap.traffic===0)score-=10;
  return Math.max(0,Math.min(100,score));
};
const healthColor = s => s>=80?C.accent3:s>=60?C.accent4:C.accent5;
const healthLabel = s => s>=80?"Good":s>=60?"Fair":"Poor";

// ─── Components ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color=C.accent }) => (
  <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px"}}>
    <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{label}</div>
    <div style={{fontSize:26,fontWeight:700,color,lineHeight:1}}>{value}</div>
    {sub&&<div style={{fontSize:12,color:C.muted,marginTop:4}}>{sub}</div>}
  </div>
);

const ChartCard = ({ title, sub, children }) => (
  <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:20}}>
    <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:2}}>{title}</div>
    {sub&&<div style={{fontSize:11,color:C.muted,marginBottom:14}}>{sub}</div>}
    {!sub&&<div style={{marginBottom:14}}/>}
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if(!active||!payload?.length)return null;
  return(
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontSize:12}}>
      {label&&<div style={{color:C.muted,marginBottom:6,maxWidth:200}}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{color:p.color||C.text,marginBottom:2}}>
          {p.name}: <span style={{fontWeight:700}}>{formatter?formatter(p.value):fmtNum(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Leaflet Map Component ────────────────────────────────────────────────────
const MapView = ({ apList }) => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);
  const [mapMode, setMapMode] = useState("clients");
  const [mapReady, setMapReady] = useState(false);

  const validAPs = useMemo(() => apList.filter(ap =>
    ap.lat != null && ap.lng != null &&
    !isNaN(ap.lat) && !isNaN(ap.lng) &&
    isFinite(ap.lat) && isFinite(ap.lng) &&
    ap.lat >= -90 && ap.lat <= 90 &&
    ap.lng >= -180 && ap.lng <= 180
  ), [apList]);

  const getColor = (ap, mode) => {
    if(mode==="health") return healthColor(ap.health);
    if(mode==="rssi") return ap.avg_rssi==null?"#5a7292":ap.avg_rssi>-65?"#10b981":ap.avg_rssi>-75?"#f59e0b":"#ef4444";
    const maxVal = Math.max(...validAPs.map(a=>mode==="clients"?a.clients:a.traffic),1);
    const ratio = (mode==="clients"?ap.clients:ap.traffic)/maxVal;
    return ratio>0.66?"#ef4444":ratio>0.33?"#f59e0b":"#10b981";
  };

  const getRadius = (ap, mode) => {
    if(mode==="health"||mode==="rssi") return 10;
    const maxVal = Math.max(...validAPs.map(a=>mode==="clients"?a.clients:a.traffic),1);
    const ratio = (mode==="clients"?ap.clients:ap.traffic)/maxVal;
    return 6 + Math.sqrt(ratio)*20;
  };

  // Load Leaflet CSS + JS once
  useEffect(() => {
    if(document.getElementById("leaflet-css")) { setMapReady(true); return; }
    const style = document.createElement("style");
    style.id = "leaflet-fix";
    style.textContent = `.leaflet-container{background:#0a1520!important;} .leaflet-tile-container img{background:#0a1520;}`;
    document.head.appendChild(style);
    const link = document.createElement("link");
    link.id = "leaflet-css";
    link.rel = "stylesheet";
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  }, []);

  // Init map
  useEffect(() => {
    if(!mapReady||!mapRef.current||leafletMapRef.current||!validAPs.length) return;
    const L = window.L;
    const lats = validAPs.map(a=>a.lat);
    const lngs = validAPs.map(a=>a.lng);
    const centerLat = (Math.min(...lats)+Math.max(...lats))/2;
    const centerLng = (Math.min(...lngs)+Math.max(...lngs))/2;
    const map = L.map(mapRef.current, { zoomControl:true, attributionControl:true }).setView([centerLat,centerLng],8);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{
      attribution:'© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom:19
    }).addTo(map);
    leafletMapRef.current = map;
    // fit bounds
    const bounds = L.latLngBounds(validAPs.map(a=>[a.lat,a.lng]));
    map.fitBounds(bounds, {padding:[40,40]});
    return () => { map.remove(); leafletMapRef.current=null; };
  }, [mapReady, validAPs]);

  // Update markers when mode changes
  useEffect(() => {
    if(!leafletMapRef.current||!mapReady||!validAPs.length) return;
    const L = window.L;
    const map = leafletMapRef.current;
    // Remove old markers
    markersRef.current.forEach(m=>m.remove());
    markersRef.current = [];
    // Add new markers
    validAPs.forEach(ap => {
      const color = getColor(ap, mapMode);
      const radius = getRadius(ap, mapMode);
      const circle = L.circleMarker([ap.lat, ap.lng], {
        radius, fillColor:color, color:"#fff", weight:1,
        opacity:0.9, fillOpacity:0.8
      }).addTo(map);
      circle.bindPopup(`
        <div style="font-family:Inter,sans-serif;min-width:180px">
          <div style="font-weight:700;font-size:13px;margin-bottom:4px">${ap.ap_name}</div>
          <div style="color:#666;font-size:11px;margin-bottom:8px">${ap.location}</div>
          <div style="font-size:12px"><b>Clients:</b> ${ap.clients.toLocaleString()}</div>
          <div style="font-size:12px"><b>Sessions:</b> ${ap.sessions.toLocaleString()}</div>
          <div style="font-size:12px"><b>Traffic:</b> ${fmt(ap.traffic)}</div>
          <div style="font-size:12px"><b>Avg RSSI:</b> ${ap.avg_rssi!=null?`${ap.avg_rssi.toFixed(1)} dBm`:"—"}</div>
          <div style="font-size:12px"><b>Health:</b> <span style="color:${healthColor(ap.health)};font-weight:700">${healthLabel(ap.health)} (${ap.health})</span></div>
        </div>
      `);
      markersRef.current.push(circle);
    });
  }, [mapMode, mapReady, validAPs]);

  return(
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:C.text}}>Network Coverage Map</div>
          <div style={{fontSize:11,color:C.muted,marginTop:2}}>{validAPs.length} APs plotted on map</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {["clients","traffic","rssi","health"].map(m=>(
            <button key={m} onClick={()=>setMapMode(m)} style={{
              background:mapMode===m?C.accent:"transparent", border:`1px solid ${mapMode===m?C.accent:C.border}`,
              borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11,
              color:mapMode===m?"#fff":C.muted, textTransform:"capitalize"
            }}>{m}</button>
          ))}
        </div>
      </div>
      <div ref={mapRef} style={{height:650,borderRadius:8,overflow:"hidden",zIndex:0}}/>
      <div style={{display:"flex",gap:20,marginTop:12,fontSize:11,color:C.muted,flexWrap:"wrap"}}>
        {mapMode==="rssi"||mapMode==="health"?(<>
          <span><span style={{color:C.accent3}}>●</span> <b style={{color:C.accent3}}>Good</b> — Strong signal / healthy AP</span>
          <span><span style={{color:C.accent4}}>●</span> <b style={{color:C.accent4}}>Fair</b> — Moderate signal / needs attention</span>
          <span><span style={{color:C.accent5}}>●</span> <b style={{color:C.accent5}}>Poor</b> — Weak signal / critical issue</span>
        </>):(<>
          <span><span style={{color:C.accent3}}>●</span> <b style={{color:C.accent3}}>Low</b> — Below average {mapMode}</span>
          <span><span style={{color:C.accent4}}>●</span> <b style={{color:C.accent4}}>Medium</b> — Average {mapMode}</span>
          <span><span style={{color:C.accent5}}>●</span> <b style={{color:C.accent5}}>High</b> — Above average {mapMode}</span>
          <span style={{marginLeft:"auto"}}>Circle size = {mapMode} · Click any circle for details</span>
        </>)}
      </div>
    </div>
  );
};

// ─── Location Drilldown Modal ─────────────────────────────────────────────────
const DrilldownModal = ({ ap, onClose }) => {
  if(!ap) return null;
  const score = ap.health;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:28,maxWidth:560,width:"100%",maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:C.text}}>{ap.ap_name}</div>
            <div style={{fontSize:13,color:C.muted,marginTop:3}}>{ap.location}</div>
            {ap.location_address&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>{ap.location_address}</div>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:28,fontWeight:800,color:healthColor(score)}}>{score}</div>
              <div style={{fontSize:10,color:healthColor(score),fontWeight:600}}>{healthLabel(score)} Health</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:20}}>✕</button>
          </div>
        </div>
        {/* Health bar */}
        <div style={{background:C.dim,borderRadius:4,height:8,marginBottom:20}}>
          <div style={{width:`${score}%`,background:healthColor(score),height:8,borderRadius:4,transition:"width 0.5s"}}/>
        </div>
        {/* Stats grid */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
          {[
            {label:"Clients",value:fmtNum(ap.clients),color:C.accent},
            {label:"Sessions",value:fmtNum(ap.sessions),color:C.accent2},
            {label:"Total Traffic",value:fmt(ap.traffic),color:C.accent3},
            {label:"Avg Session",value:fmtSec(ap.avg_duration),color:C.accent4},
            {label:"Avg RSSI",value:ap.avg_rssi!=null?`${ap.avg_rssi.toFixed(1)} dBm`:"—",color:ap.avg_rssi>-65?C.accent3:ap.avg_rssi>-75?C.accent4:C.accent5},
            {label:"TX / RX",value:`${fmt(ap.tx)} / ${fmt(ap.rx)}`,color:C.muted},
          ].map(({label,value,color})=>(
            <div key={label} style={{background:C.surface,borderRadius:8,padding:"12px 14px"}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{label}</div>
              <div style={{fontSize:17,fontWeight:700,color}}>{value}</div>
            </div>
          ))}
        </div>
        {/* Band breakdown */}
        {Object.keys(ap.bands).length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Band Usage</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {Object.entries(ap.bands).map(([band,count])=>(
                <div key={band} style={{background:C.dim,borderRadius:6,padding:"4px 10px",fontSize:12}}>
                  <span style={{color:BAND_COLORS[band]||C.accent}}>●</span> {band}: <b style={{color:C.text}}>{count}</b>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* SSID breakdown */}
        {Object.keys(ap.ssids).length>0&&(
          <div>
            <div style={{fontSize:12,color:C.muted,marginBottom:8}}>SSID Usage</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {Object.entries(ap.ssids).sort((a,b)=>b[1]-a[1]).map(([ssid,count],i)=>(
                <div key={ssid} style={{background:C.dim,borderRadius:6,padding:"4px 10px",fontSize:12}}>
                  <span style={{color:SSID_COLORS[i%SSID_COLORS.length]}}>●</span> {ssid}: <b style={{color:C.text}}>{count}</b>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Coordinates */}
        {ap.lat&&<div style={{marginTop:16,fontSize:11,color:C.muted}}>📍 {ap.lat?.toFixed(5)}, {ap.lng?.toFixed(5)}</div>}
      </div>
    </div>
  );
};

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [clientsData, setClientsData] = useState(null);
  const [sessionsData, setSessionsData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState({ clients:false, sessions:false });
  const [apSearch, setApSearch] = useState("");
  const [apSort, setApSort] = useState("clients");
  const [selectedAP, setSelectedAP] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // ── Auth state (Azure Entra ID) ──────────────────────────────────────────────
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError]   = useState("");

  const getToken = () => localStorage.getItem("wdt") || "";
  const authH = () => ({ "Content-Type":"application/json", "Authorization": `Bearer ${getToken()}` });

  // Handle OAuth2 callback: token arrives in URL fragment (#token=...&name=...)
  useEffect(() => {
    const hash = window.location.hash;
    if(hash.includes("token=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const token  = params.get("token");
      const name   = params.get("name") || "";
      if(token) {
        localStorage.setItem("wdt", token);
        // Clean the fragment from URL without reload
        window.history.replaceState(null, "", window.location.pathname);
        setIsLoggedIn(true);
        setCurrentUser({ name: decodeURIComponent(name) });
        return;
      }
    }
    // Check for auth error in query string
    const sp = new URLSearchParams(window.location.search);
    const ae = sp.get("auth_error");
    if(ae) {
      setAuthError(ae === "wrong_tenant"
        ? "Access denied — only @celldwell.com accounts are allowed."
        : `Authentication error: ${ae}`);
      window.history.replaceState(null, "", window.location.pathname);
    }
    // Resume existing session
    const t = getToken();
    if(!t) return;
    fetch("/api/me", { headers:{ "Authorization":`Bearer ${t}` } })
      .then(r=>r.json())
      .then(d=>{ if(d.logged_in){ setIsLoggedIn(true); setCurrentUser(d); } else { localStorage.removeItem("wdt"); } })
      .catch(()=>{});
  }, []);

  const handleLogout = async () => {
    await fetch("/api/logout", { method:"POST", headers: authH() });
    localStorage.removeItem("wdt");
    setIsLoggedIn(false);
    setCurrentUser(null);
    setClientsData(null);
    setSessionsData(null);
    // Also sign out from Microsoft
    window.location.href = `https://login.microsoftonline.com/3370d7b9-50c9-4d87-aac9-35537bb23fbc/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
  };

  // ── Auto-fetch ────────────────────────────────────────────────────────────
  const fetchFromAPI = useCallback(async (triggerSync=false) => {
    const token = localStorage.getItem("wdt") || "";
    const headers = { "Content-Type":"application/json", "Authorization": `Bearer ${token}` };
    try {
      if(triggerSync) await fetch("/api/sync", { method:"POST", headers });
      const sr = await fetch("/api/status", { headers, signal: AbortSignal.timeout(10000) });
      if(!sr.ok) { setApiStatus(false); return; }
      const status = await sr.json();
      setApiStatus(true);
      if(status.clients_available) {
        setLoading(p=>({...p,clients:true}));
        const r = await fetch("/api/clients", { headers });
        if(r.ok) { const d = await r.json(); setClientsData(d); setLastSynced(status.data?.synced_at); }
        setLoading(p=>({...p,clients:false}));
      }
      if(status.sessions_available) {
        setLoading(p=>({...p,sessions:true}));
        const r = await fetch("/api/sessions", { headers });
        if(r.ok) setSessionsData(await r.json());
        setLoading(p=>({...p,sessions:false}));
      }
    } catch(e) { setApiStatus(false); }
  }, []);

  useEffect(() => {
    if(!isLoggedIn) return;
    fetchFromAPI();
    const iv = setInterval(() => fetchFromAPI(), 60*60*1000);
    return () => clearInterval(iv);
  }, [isLoggedIn]);

  const handleManualSync = async () => { setSyncing(true); await fetchFromAPI(true); setSyncing(false); };

  const handleFile = useCallback((e, type) => {
    const file = e.target.files[0];
    if(!file) return;
    setLoading(p=>({...p,[type]:true}));
    const reader = new FileReader();
    reader.onload = ev => {
      try { const data = JSON.parse(ev.target.result); if(type==="clients")setClientsData(data); else setSessionsData(data); }
      catch { alert("Invalid JSON"); }
      setLoading(p=>({...p,[type]:false}));
    };
    reader.readAsText(file);
  }, []);

  const analytics = useMemo(() => {
    if(!clientsData&&!sessionsData) return null;
    const clients = clientsData||[];
    const sessions = sessionsData||[];

    // AP stats
    const apMap = {};
    clients.forEach(c => {
      const ap = c.ap_name||"Unknown";
      if(!apMap[ap]) apMap[ap] = {
        ap_name:ap, location:c.location_name||"Unknown", location_id:c.location_id,
        location_address:c.location_address||null,
        lat:c.location_latitude!=null&&!isNaN(parseFloat(c.location_latitude))?parseFloat(c.location_latitude):null,
        lng:c.location_longitude!=null&&!isNaN(parseFloat(c.location_longitude))?parseFloat(c.location_longitude):null,
        clients:0, traffic:0, rssi_sum:0, rssi_count:0, tx:0, rx:0, bands:{}, ssids:{}
      };
      apMap[ap].clients++;
      apMap[ap].traffic+=(c.traffic_total_bytes||0);
      apMap[ap].tx+=(c.traffic_tx_bytes||0);
      apMap[ap].rx+=(c.traffic_rx_bytes||0);
      if(c.signal_rssi!=null){apMap[ap].rssi_sum+=c.signal_rssi;apMap[ap].rssi_count++;}
      const band=c.signal_band||"Unknown"; apMap[ap].bands[band]=(apMap[ap].bands[band]||0)+1;
      const ssid=c.network_ssid||"Unknown"; apMap[ap].ssids[ssid]=(apMap[ap].ssids[ssid]||0)+1;
    });

    const apSessionMap = {};
    sessions.forEach(s => {
      const ap=s.ap_name||"Unknown";
      if(!apSessionMap[ap]) apSessionMap[ap]={sessions:0,duration_sum:0,duration_count:0};
      apSessionMap[ap].sessions++;
      if(s.session_duration_sec!=null){apSessionMap[ap].duration_sum+=s.session_duration_sec;apSessionMap[ap].duration_count++;}
    });

    const apList = Object.values(apMap).map(ap => {
      const avgRssi=ap.rssi_count>0?ap.rssi_sum/ap.rssi_count:null;
      const score=healthScore({...ap,avg_rssi:avgRssi});
      return{
        ...ap,
        avg_rssi:avgRssi,
        sessions:apSessionMap[ap.ap_name]?.sessions||0,
        avg_duration:apSessionMap[ap.ap_name]?.duration_count>0?apSessionMap[ap.ap_name].duration_sum/apSessionMap[ap.ap_name].duration_count:null,
        health:score
      };
    });

    // Location stats
    const locMap = {};
    clients.forEach(c => {
      const loc=c.location_name||"Unknown";
      if(!locMap[loc]) locMap[loc]={location:loc,clients:0,traffic:0,aps:new Set()};
      locMap[loc].clients++;
      locMap[loc].traffic+=(c.traffic_total_bytes||0);
      locMap[loc].aps.add(c.ap_name);
    });
    const locationList=Object.values(locMap).map(l=>({...l,aps:l.aps.size}));

    // RSSI buckets
    const rssiBuckets=[
      {range:"≥ -50 (Excellent)",min:-50,max:0,count:0,color:C.accent3},
      {range:"-60 to -51 (Good)",min:-60,max:-51,count:0,color:C.accent},
      {range:"-70 to -61 (Fair)",min:-70,max:-61,count:0,color:C.accent4},
      {range:"-80 to -71 (Weak)",min:-80,max:-71,count:0,color:C.accent5},
      {range:"< -80 (Poor)",min:-999,max:-81,count:0,color:"#7f1d1d"},
    ];
    clients.filter(c=>c.signal_rssi!=null).forEach(c=>{
      const b=rssiBuckets.find(b=>c.signal_rssi>=b.min&&c.signal_rssi<=b.max);
      if(b)b.count++;
    });

    // SSID
    const ssidMap={};
    clients.forEach(c=>{const s=c.network_ssid||"Unknown";ssidMap[s]=(ssidMap[s]||0)+1;});
    const ssidList=Object.entries(ssidMap).map(([name,count])=>({name,count})).sort((a,b)=>b.count-a.count);

    // Band
    const bandMap={};
    clients.forEach(c=>{const b=c.signal_band||"Unknown";bandMap[b]=(bandMap[b]||0)+1;});
    const bandList=Object.entries(bandMap).map(([name,count])=>({name,count}));

    // Session duration
    const durBuckets=[{range:"<1min",count:0},{range:"1-5min",count:0},{range:"5-15min",count:0},{range:"15-30min",count:0},{range:"30-60min",count:0},{range:">1hr",count:0}];
    sessions.forEach(s=>{
      const d=s.session_duration_sec;if(d==null)return;
      if(d<60)durBuckets[0].count++;else if(d<300)durBuckets[1].count++;else if(d<900)durBuckets[2].count++;
      else if(d<1800)durBuckets[3].count++;else if(d<3600)durBuckets[4].count++;else durBuckets[5].count++;
    });

    // Hourly
    const hourMap=Array(24).fill(0);
    sessions.forEach(s=>{ if(s.session_start){const h=new Date(s.session_start).getUTCHours();if(!isNaN(h))hourMap[h]++;} });
    const hourlyData=hourMap.map((count,h)=>({hour:`${h}:00`,count}));

    // Realm / Carrier analysis
    const realmMap={};
    clients.forEach(c=>{
      const raw=c.client_realm||"Unknown";
      const mnc=raw.match(/mnc(\d+)/)?.[1];
      const mcc=raw.match(/mcc(\d+)/)?.[1];
      let carrier="Unknown";
      if(mcc==="310"||mcc==="311"||mcc==="312"){
        if(mnc==="260")carrier="T-Mobile";
        else if(mnc==="410"||mnc==="380")carrier="AT&T";
        else if(mnc==="012")carrier="Verizon";
        else if(mnc==="280"||mnc==="240")carrier="T-Mobile (MVNO)";
        else if(mnc==="030")carrier="AT&T MVNO";
        else carrier=`US Carrier (MNC ${mnc})`;
      } else if(raw==="Unknown")carrier="Unknown";
      else carrier=raw.length>30?raw.substring(0,30)+"…":raw;
      realmMap[carrier]=(realmMap[carrier]||0)+1;
    });
    const realmList=Object.entries(realmMap).map(([name,count])=>({name,count})).sort((a,b)=>b.count-a.count);
    const realmByAP={};
    clients.forEach(c=>{
      const ap=c.ap_name||"Unknown";
      const raw=c.client_realm||"Unknown";
      const mnc=raw.match(/mnc(\d+)/)?.[1];
      const mcc=raw.match(/mcc(\d+)/)?.[1];
      let carrier="Unknown";
      if(mcc==="310"||mcc==="311"||mcc==="312"){
        if(mnc==="260")carrier="T-Mobile";
        else if(mnc==="410"||mnc==="380")carrier="AT&T";
        else if(mnc==="012")carrier="Verizon";
        else carrier=`MNC ${mnc}`;
      }
      if(!realmByAP[ap])realmByAP[ap]={};
      realmByAP[ap][carrier]=(realmByAP[ap][carrier]||0)+1;
    });

    // Manufacturer
    const mfgMap={};
    clients.forEach(c=>{const m=c.client_manufacturer||"Unknown";mfgMap[m]=(mfgMap[m]||0)+1;});
    const mfgList=Object.entries(mfgMap).map(([name,count])=>({name,count})).sort((a,b)=>b.count-a.count).slice(0,10);

    // Health buckets
    const healthBuckets={good:0,fair:0,poor:0};
    apList.forEach(ap=>{
      if(ap.health>=80)healthBuckets.good++;
      else if(ap.health>=60)healthBuckets.fair++;
      else healthBuckets.poor++;
    });

    // Null stats
    const requiredFields=["location_id","location_name","location_address","location_latitude","location_longitude"];
    const nullStats={};
    requiredFields.forEach(f=>{nullStats[f]={clients:0,sessions:0};});
    clients.forEach(c=>requiredFields.forEach(f=>{if(!c[f])nullStats[f].clients++;}));
    sessions.forEach(s=>requiredFields.forEach(f=>{if(!s[f])nullStats[f].sessions++;}));

    const invalidClientMap={};
    clients.forEach(c=>{
      const missing=requiredFields.filter(f=>!c[f]);
      if(!missing.length)return;
      const key=c.ap_name||"Unknown";
      if(!invalidClientMap[key]) invalidClientMap[key]={ap_name:c.ap_name||"Unknown",location_name:c.location_name||"—",location_id:c.location_id||"—",location_address:c.location_address||"—",location_latitude:c.location_latitude??"—",location_longitude:c.location_longitude??"—",count:0,missing:new Set()};
      invalidClientMap[key].count++;
      missing.forEach(f=>invalidClientMap[key].missing.add(f));
    });
    const invalidSessionMap={};
    sessions.forEach(s=>{
      const missing=requiredFields.filter(f=>!s[f]);
      if(!missing.length)return;
      const key=s.ap_name||"Unknown";
      if(!invalidSessionMap[key]) invalidSessionMap[key]={ap_name:s.ap_name||"Unknown",location_name:s.location_name||"—",location_id:s.location_id||"—",location_address:s.location_address||"—",location_latitude:s.location_latitude??"—",location_longitude:s.location_longitude??"—",count:0,missing:new Set()};
      invalidSessionMap[key].count++;
      missing.forEach(f=>invalidSessionMap[key].missing.add(f));
    });

    const totalTraffic=clients.reduce((s,c)=>s+(c.traffic_total_bytes||0),0);
    const validRssi=clients.filter(c=>c.signal_rssi!=null);
    const avgRssi=validRssi.length?validRssi.reduce((s,c)=>s+c.signal_rssi,0)/validRssi.length:null;
    const totalSessions=sessions.length;
    const validDur=sessions.filter(s=>s.session_duration_sec!=null);
    const avgDuration=validDur.length?validDur.reduce((s,c)=>s+c.session_duration_sec,0)/validDur.length:null;
    const uniqueLocations=new Set(clients.map(c=>c.location_id)).size;
    const uniqueAPs=Object.keys(apMap).length;
    const totalInvalid=clients.filter(c=>requiredFields.some(f=>!c[f])).length;
    const totalInvalidSessions=sessions.filter(s=>requiredFields.some(f=>!s[f])).length;
    const invalidClients=Object.values(invalidClientMap).sort((a,b)=>b.count-a.count);
    const invalidSessions=Object.values(invalidSessionMap).sort((a,b)=>b.count-a.count);

    return{
      clients,sessions,apList,locationList,ssidList,bandList,rssiBuckets,durBuckets,
      hourlyData,nullStats,mfgList,realmList,realmByAP,healthBuckets,
      totalTraffic,avgRssi,totalSessions,avgDuration,uniqueLocations,uniqueAPs,
      totalClients:clients.length,totalInvalid,invalidClients,invalidSessions,totalInvalidSessions
    };
  }, [clientsData, sessionsData]);

  const TABS=[
    {id:"overview",label:"Overview"},{id:"map",label:"Map"},
    {id:"aps",label:"Access Points"},
    {id:"traffic",label:"Traffic"},{id:"signal",label:"Signal / RSSI"},
    {id:"sessions",label:"Sessions"},{id:"validation",label:"Data Quality"},
  ];

  const sortedAPs = useMemo(()=>{
    if(!analytics)return[];
    return[...analytics.apList]
      .filter(ap=>ap.ap_name.toLowerCase().includes(apSearch.toLowerCase())||ap.location.toLowerCase().includes(apSearch.toLowerCase()))
      .sort((a,b)=>{
        if(apSort==="clients")return b.clients-a.clients;
        if(apSort==="traffic")return b.traffic-a.traffic;
        if(apSort==="rssi")return(a.avg_rssi??-999)-(b.avg_rssi??-999);
        if(apSort==="sessions")return b.sessions-a.sessions;
        if(apSort==="health")return b.health-a.health;
        return 0;
      });
  },[analytics,apSearch,apSort]);

  const top10 = (key) => analytics?[...analytics.apList].sort((a,b)=>b[key]-a[key]).slice(0,10):[];

  if(!isLoggedIn) {
    return(
      <div style={{background:C.bg,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',system-ui,sans-serif"}}>
        <style>{`*{box-sizing:border-box;margin:0;padding:0} body,html{background:#080d14;}`}</style>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"48px 52px",width:420,boxShadow:"0 20px 60px rgba(0,0,0,0.5)",textAlign:"center"}}>
          <div style={{marginBottom:36}}>
            <div style={{fontSize:11,color:C.muted,letterSpacing:2,fontWeight:600,marginBottom:10}}>CELLDWELL</div>
            <div style={{fontSize:24,fontWeight:700,color:C.text,marginBottom:6}}>WiFi Analytics</div>
            <div style={{fontSize:13,color:C.muted}}>Sign in with your CellDwell Microsoft account</div>
          </div>

          {authError&&(
            <div style={{background:"#1a0505",border:`1px solid ${C.accent5}`,borderRadius:8,padding:"12px 16px",fontSize:13,color:"#fca5a5",marginBottom:24,textAlign:"left"}}>
              {authError}
            </div>
          )}

          <a href="/auth/login" style={{
            display:"flex",alignItems:"center",justifyContent:"center",gap:12,
            width:"100%",padding:"13px 20px",
            background:"#fff",border:"1px solid #d1d5db",borderRadius:8,
            color:"#1f2937",fontSize:14,fontWeight:600,textDecoration:"none",
            cursor:"pointer",transition:"background 0.15s"
          }}
          onMouseOver={e=>e.currentTarget.style.background="#f3f4f6"}
          onMouseOut={e=>e.currentTarget.style.background="#fff"}>
            <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </a>

          <div style={{fontSize:12,color:C.muted,marginTop:20}}>
            Access restricted to @celldwell.com accounts
          </div>
        </div>
      </div>
    );
  }

  // Change Password removed — password managed by Microsoft/Entra ID

  if(!analytics){
    return(
      <div style={{background:C.bg,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',system-ui,sans-serif"}}>
        <style>{`*{box-sizing:border-box;margin:0;padding:0} body,html{background:#080d14;} @keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{textAlign:"center",maxWidth:440}}>
          {apiStatus===false ? (
            <>
              <div style={{fontSize:16,fontWeight:600,color:C.accent5,marginBottom:8}}>Could not connect to API</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:24}}>Upload JSON files manually below</div>
            </>
          ) : (
            <>
              <div style={{width:40,height:40,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.accent}`,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 20px"}}/>
              <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:6}}>Loading data...</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:24}}>Fetching latest files from Azure Blob Storage</div>
            </>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {["clients","sessions"].map(type=>(
              <label key={type} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,cursor:"pointer",display:"block",textAlign:"left"}}>
                <input type="file" accept=".json" onChange={e=>handleFile(e,type)} style={{display:"none"}}/>
                <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:1,fontWeight:600}}>{type}</div>
                <div style={{fontSize:13,color:loading[type]?C.accent:C.muted}}>{loading[type]?"Loading...":"Upload JSON"}</div>
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return(
    <div style={{background:C.bg,minHeight:"100vh",fontFamily:"'Inter',system-ui,sans-serif",color:C.text}}>
      {/* Drilldown Modal */}
      <DrilldownModal ap={selectedAP} onClose={()=>setSelectedAP(null)}/>

      {/* Header */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:56}}>
        <style>{`*{box-sizing:border-box;} body,html{margin:0;padding:0;background:#080d14;} .leaflet-container{background:#0a1520!important;}`}</style>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontWeight:700,fontSize:15,color:C.text,letterSpacing:0.5}}>WiFi Analytics</span>
          <span style={{fontSize:12,color:C.muted,marginLeft:4}}>
            {clientsData?`${fmtNum(analytics.totalClients)} clients`:""}{sessionsData?` · ${fmtNum(analytics.totalSessions)} sessions`:""}
          </span>
          {lastSynced&&<span style={{fontSize:11,color:C.muted,marginLeft:4}}>· {new Date(lastSynced).toLocaleDateString()}</span>}
          {apiStatus===true&&<span style={{fontSize:10,background:"#0a2a1a",color:C.accent3,border:`1px solid #10b981`,borderRadius:4,padding:"2px 8px",fontWeight:600}}>LIVE</span>}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={handleManualSync} disabled={syncing} style={{background:syncing?"#1e2433":C.accent,border:"none",borderRadius:6,padding:"5px 14px",cursor:syncing?"not-allowed":"pointer",fontSize:12,color:"#fff",fontWeight:600}}>
            {syncing?"Syncing...":"Sync"}
          </button>
          {["clients","sessions"].map(type=>{
            const has=type==="clients"?!!clientsData:!!sessionsData;
            return(
              <label key={type} style={{background:has?"#0a2a1a":C.card,border:`1px solid ${has?"#10b981":C.border}`,borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12,color:has?"#10b981":C.muted}}>
                <input type="file" accept=".json" onChange={e=>handleFile(e,type)} style={{display:"none"}}/>
                {has?`+ ${type}`:`+ ${type}`}
              </label>
            );
          })}
          {currentUser&&<span style={{fontSize:12,color:C.muted,padding:"4px 8px",background:C.card,borderRadius:6,border:`1px solid ${C.border}`}}>{currentUser.name||currentUser.username}</span>}
          <button onClick={handleLogout} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 12px",cursor:"pointer",fontSize:12,color:C.muted}}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Nav */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"0 24px",display:"flex",gap:0,overflowX:"hidden"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{
            background:"none",border:"none",cursor:"pointer",padding:"12px 16px",fontSize:13,fontWeight:500,whiteSpace:"nowrap",
            color:activeTab===t.id?C.accent:C.muted,
            borderBottom:activeTab===t.id?`2px solid ${C.accent}`:"2px solid transparent",marginBottom:-1
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{padding:24}}>

        {/* OVERVIEW */}
        {activeTab==="overview"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:24}}>
              <StatCard label="Total Clients" value={fmtNum(analytics.totalClients)} color={C.accent}/>
              <StatCard label="Total Sessions" value={fmtNum(analytics.totalSessions)} color={C.accent2}/>
              <StatCard label="Locations" value={fmtNum(analytics.uniqueLocations)} color={C.accent3}/>
              <StatCard label="Access Points" value={fmtNum(analytics.uniqueAPs)} color={C.accent4}/>
              <StatCard label="Total Traffic" value={fmt(analytics.totalTraffic)} color={C.accent}/>
              <StatCard label="Avg Session" value={fmtSec(analytics.avgDuration)} color={C.accent2}/>
              <StatCard label="Avg RSSI" value={analytics.avgRssi?`${analytics.avgRssi.toFixed(1)} dBm`:"—"} color={analytics.avgRssi>-65?C.accent3:analytics.avgRssi>-75?C.accent4:C.accent5}/>
              <StatCard label="AP Health" value={`${analytics.healthBuckets.good}/${analytics.uniqueAPs}`} sub="APs in good health" color={C.accent3}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              <ChartCard title="Top 10 Locations by Clients" sub="Client connections per venue">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.locationList.sort((a,b)=>b.clients-a.clients).slice(0,10)} layout="vertical" margin={{left:8}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}}/>
                    <YAxis type="category" dataKey="location" tick={{fill:C.muted,fontSize:10}} width={130}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="clients" fill={C.accent} radius={[0,4,4,0]} name="Clients"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="SSID Distribution" sub="Client connections per network">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={analytics.ssidList} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                      {analytics.ssidList.map((_,i)=><Cell key={i} fill={SSID_COLORS[i%SSID_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip content={<CustomTooltip/>}/>
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <ChartCard title="WiFi Band Distribution" sub="2.4GHz vs 5GHz usage">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={analytics.bandList}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim}/>
                    <XAxis dataKey="name" tick={{fill:C.muted,fontSize:11}}/>
                    <YAxis tick={{fill:C.muted,fontSize:11}}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="count" name="Clients" radius={[4,4,0,0]}>
                      {analytics.bandList.map((b,i)=><Cell key={i} fill={BAND_COLORS[b.name]||C.accent2}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Session Activity by Hour (UTC)" sub="When clients are most active">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={analytics.hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim}/>
                    <XAxis dataKey="hour" tick={{fill:C.muted,fontSize:10}} interval={3}/>
                    <YAxis tick={{fill:C.muted,fontSize:11}}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Line type="monotone" dataKey="count" stroke={C.accent3} strokeWidth={2} dot={false} name="Sessions"/>
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>
        )}

        {/* MAP TAB */}
        {activeTab==="map"&&(
          <div>
            <MapView apList={analytics.apList}/>
            <div style={{marginTop:16,display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
              <StatCard label="APs on Map" value={fmtNum(analytics.apList.filter(a=>a.lat!=null).length)} sub={`of ${analytics.uniqueAPs} total APs`} color={C.accent}/>
              <StatCard label="APs Missing Coords" value={fmtNum(analytics.apList.filter(a=>a.lat==null).length)} color={analytics.apList.filter(a=>a.lat==null).length>0?C.accent5:C.accent3}/>
              <StatCard label="Good Health APs" value={fmtNum(analytics.healthBuckets.good)} sub="score ≥ 80" color={C.accent3}/>
            </div>
          </div>
        )}


        {/* ACCESS POINTS TAB */}
        {activeTab==="aps"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
              <ChartCard title="Top 10 APs by Client Count" sub="Click a row to see full details">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={top10("clients")} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}}/>
                    <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="clients" fill={C.accent} radius={[0,4,4,0]} name="Clients"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Top 10 APs by Sessions">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={top10("sessions")} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}}/>
                    <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="sessions" fill={C.accent2} radius={[0,4,4,0]} name="Sessions"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:C.text}}>All Access Points</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:2}}>Click any row to see full details · {sortedAPs.length} APs</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input placeholder="Search AP or location..." value={apSearch} onChange={e=>setApSearch(e.target.value)}
                    style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 12px",color:C.text,fontSize:12,width:220}}/>
                  <select value={apSort} onChange={e=>setApSort(e.target.value)}
                    style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",color:C.text,fontSize:12}}>
                    <option value="clients">Clients</option>
                    <option value="traffic">Traffic</option>
                    <option value="sessions">Sessions</option>
                    <option value="rssi">RSSI</option>
                    <option value="health">Health</option>
                  </select>
                </div>
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{borderBottom:`1px solid ${C.border}`}}>
                      {["AP Name","Location","Health","Clients","Sessions","Traffic","Avg RSSI","Avg Duration"].map(h=>(
                        <th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAPs.slice(0,200).map((ap,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${C.dim}`,cursor:"pointer"}} onClick={()=>setSelectedAP(ap)}>
                        <td style={{padding:"8px 12px",color:C.text}}>{ap.ap_name}</td>
                        <td style={{padding:"8px 12px",color:C.muted}}>{ap.location}</td>
                        <td style={{padding:"8px 12px"}}>
                          <span style={{color:healthColor(ap.health),fontWeight:600,fontSize:11}}>{healthLabel(ap.health)} ({ap.health})</span>
                        </td>
                        <td style={{padding:"8px 12px",color:C.accent,fontWeight:600}}>{fmtNum(ap.clients)}</td>
                        <td style={{padding:"8px 12px",color:C.accent2}}>{fmtNum(ap.sessions)}</td>
                        <td style={{padding:"8px 12px",color:C.accent3}}>{fmt(ap.traffic)}</td>
                        <td style={{padding:"8px 12px",color:ap.avg_rssi==null?C.muted:ap.avg_rssi>-65?C.accent3:ap.avg_rssi>-75?C.accent4:C.accent5}}>
                          {ap.avg_rssi!=null?`${ap.avg_rssi.toFixed(1)} dBm`:"—"}
                        </td>
                        <td style={{padding:"8px 12px",color:C.muted}}>{fmtSec(ap.avg_duration)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sortedAPs.length>200&&<div style={{textAlign:"center",color:C.muted,padding:12,fontSize:12}}>Showing 200 of {sortedAPs.length} APs</div>}
              </div>
            </div>
          </div>
        )}

        {/* TRAFFIC TAB */}
        {activeTab==="traffic"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
              <StatCard label="Total Traffic" value={fmt(analytics.totalTraffic)} color={C.accent}/>
              <StatCard label="Total TX" value={fmt(analytics.apList.reduce((s,a)=>s+a.tx,0))} color={C.accent3}/>
              <StatCard label="Total RX" value={fmt(analytics.apList.reduce((s,a)=>s+a.rx,0))} color={C.accent2}/>
              <StatCard label="Avg per Client" value={fmt(analytics.totalTraffic/(analytics.totalClients||1))} color={C.accent4}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              <ChartCard title="Top 10 APs by Traffic">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={top10("traffic")} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}} tickFormatter={v=>fmt(v)}/>
                    <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                    <Tooltip content={<CustomTooltip formatter={fmt}/>}/>
                    <Bar dataKey="traffic" fill={C.accent3} radius={[0,4,4,0]} name="Traffic"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="TX vs RX — Top 10 APs" sub="Upload vs download traffic">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={top10("traffic")} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}} tickFormatter={v=>fmt(v)}/>
                    <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                    <Tooltip content={<CustomTooltip formatter={fmt}/>}/>
                    <Bar dataKey="tx" fill={C.accent3} radius={[0,2,2,0]} name="TX"/>
                    <Bar dataKey="rx" fill={C.accent2} radius={[0,2,2,0]} name="RX"/>
                    <Legend wrapperStyle={{fontSize:11,color:C.muted}}/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <ChartCard title="Top 10 Locations by Traffic">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.locationList.sort((a,b)=>b.traffic-a.traffic).slice(0,10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.dim}/>
                  <XAxis dataKey="location" tick={{fill:C.muted,fontSize:10}} angle={-20} textAnchor="end" height={50}/>
                  <YAxis tick={{fill:C.muted,fontSize:11}} tickFormatter={v=>fmt(v)}/>
                  <Tooltip content={<CustomTooltip formatter={fmt}/>}/>
                  <Bar dataKey="traffic" fill={C.accent4} radius={[4,4,0,0]} name="Traffic"/>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        {/* SIGNAL TAB */}
        {activeTab==="signal"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
              <StatCard label="Avg RSSI" value={analytics.avgRssi?`${analytics.avgRssi.toFixed(1)} dBm`:"—"} color={analytics.avgRssi>-65?C.accent3:analytics.avgRssi>-75?C.accent4:C.accent5}/>
              <StatCard label="Excellent Signal" value={fmtNum(analytics.rssiBuckets[0].count)} sub="≥ -50 dBm" color={C.accent3}/>
              <StatCard label="Good Signal" value={fmtNum(analytics.rssiBuckets[1].count)} sub="-60 to -51 dBm" color={C.accent}/>
              <StatCard label="Poor Signal" value={fmtNum(analytics.rssiBuckets[3].count+analytics.rssiBuckets[4].count)} sub="< -70 dBm" color={C.accent5}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              <ChartCard title="Signal Quality Distribution">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.rssiBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim}/>
                    <XAxis dataKey="range" tick={{fill:C.muted,fontSize:9}} angle={-15} textAnchor="end" height={60}/>
                    <YAxis tick={{fill:C.muted,fontSize:11}}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="count" radius={[4,4,0,0]} name="Clients">
                      {analytics.rssiBuckets.map((b,i)=><Cell key={i} fill={b.color}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Worst 10 APs — Signal" sub="Need repositioning or attention">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={[...analytics.apList].filter(a=>a.avg_rssi!=null).sort((a,b)=>a.avg_rssi-b.avg_rssi).slice(0,10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}} domain={[-100,0]}/>
                    <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                    <Tooltip content={<CustomTooltip formatter={v=>`${v.toFixed(1)} dBm`}/>}/>
                    <Bar dataKey="avg_rssi" fill={C.accent5} radius={[0,4,4,0]} name="Avg RSSI"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <ChartCard title="Best 10 APs — Signal">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={[...analytics.apList].filter(a=>a.avg_rssi!=null).sort((a,b)=>b.avg_rssi-a.avg_rssi).slice(0,10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.muted,fontSize:11}} domain={[-100,0]}/>
                    <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                    <Tooltip content={<CustomTooltip formatter={v=>`${v.toFixed(1)} dBm`}/>}/>
                    <Bar dataKey="avg_rssi" fill={C.accent3} radius={[0,4,4,0]} name="Avg RSSI"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

            </div>
          </div>
        )}

        {/* SESSIONS TAB */}
        {activeTab==="sessions"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
              <StatCard label="Total Sessions" value={fmtNum(analytics.totalSessions)} color={C.accent2}/>
              <StatCard label="Avg Duration" value={fmtSec(analytics.avgDuration)} color={C.accent}/>
              <StatCard label="Sessions > 1hr" value={fmtNum(analytics.durBuckets[5].count)} color={C.accent3}/>
              <StatCard label="Sessions < 1min" value={fmtNum(analytics.durBuckets[0].count)} color={C.accent4}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              <ChartCard title="Session Duration Distribution">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.durBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim}/>
                    <XAxis dataKey="range" tick={{fill:C.muted,fontSize:10}}/>
                    <YAxis tick={{fill:C.muted,fontSize:11}}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="count" fill={C.accent2} radius={[4,4,0,0]} name="Sessions"/>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Session Activity by Hour (UTC)">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={analytics.hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.dim}/>
                    <XAxis dataKey="hour" tick={{fill:C.muted,fontSize:10}} interval={2}/>
                    <YAxis tick={{fill:C.muted,fontSize:11}}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Line type="monotone" dataKey="count" stroke={C.accent3} strokeWidth={2} dot={false} name="Sessions"/>
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <ChartCard title="Top 10 APs by Avg Session Duration" sub="Where clients stay connected longest">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[...analytics.apList].filter(a=>a.avg_duration!=null).sort((a,b)=>b.avg_duration-a.avg_duration).slice(0,10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={C.dim} horizontal={false}/>
                  <XAxis type="number" tick={{fill:C.muted,fontSize:11}} tickFormatter={v=>fmtSec(v)}/>
                  <YAxis type="category" dataKey="ap_name" tick={{fill:C.muted,fontSize:9}} width={150}/>
                  <Tooltip content={<CustomTooltip formatter={fmtSec}/>}/>
                  <Bar dataKey="avg_duration" fill={C.accent4} radius={[0,4,4,0]} name="Avg Duration"/>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        {/* DATA QUALITY TAB */}
        {activeTab==="validation"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
              <StatCard label="Total Client Records" value={fmtNum(analytics.totalClients)} color={C.accent}/>
              <StatCard label="Total Session Records" value={fmtNum(analytics.totalSessions)} color={C.accent2}/>
              <StatCard label="Client Issues" value={fmtNum(analytics.totalInvalid)} sub="records with null fields" color={analytics.totalInvalid>0?C.accent5:C.accent3}/>
              <StatCard label="Data Quality" value={`${(((analytics.totalClients-analytics.totalInvalid)/analytics.totalClients)*100).toFixed(1)}%`} color={analytics.totalInvalid===0?C.accent3:C.accent4}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
              <ChartCard title="Null Fields — Clients" sub="Missing required location fields">
                <div>{Object.entries(analytics.nullStats).map(([field,stats])=>(
                  <div key={field} style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:12,color:C.muted}}>{field}</span>
                      <span style={{fontSize:12,color:stats.clients>0?C.accent5:C.accent3,fontWeight:600}}>{stats.clients>0?`${fmtNum(stats.clients)} missing`:"✓ All present"}</span>
                    </div>
                    <div style={{background:C.dim,borderRadius:4,height:6}}>
                      <div style={{width:`${((analytics.totalClients-stats.clients)/analytics.totalClients)*100}%`,background:stats.clients>0?C.accent5:C.accent3,height:6,borderRadius:4}}/>
                    </div>
                  </div>
                ))}</div>
              </ChartCard>
              <ChartCard title="Null Fields — Sessions" sub="Missing required location fields">
                <div>{Object.entries(analytics.nullStats).map(([field,stats])=>(
                  <div key={field} style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:12,color:C.muted}}>{field}</span>
                      <span style={{fontSize:12,color:stats.sessions>0?C.accent5:C.accent3,fontWeight:600}}>{stats.sessions>0?`${fmtNum(stats.sessions)} missing`:"✓ All present"}</span>
                    </div>
                    <div style={{background:C.dim,borderRadius:4,height:6}}>
                      <div style={{width:`${analytics.totalSessions>0?((analytics.totalSessions-stats.sessions)/analytics.totalSessions)*100:100}%`,background:stats.sessions>0?C.accent5:C.accent3,height:6,borderRadius:4}}/>
                    </div>
                  </div>
                ))}</div>
              </ChartCard>
            </div>
            {analytics.invalidClients.length>0&&(
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:20,marginBottom:16}}>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.text}}>Client Records with Missing Fields</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:2}}>{analytics.invalidClients.length} APs affected — {fmtNum(analytics.totalInvalid)} total records</div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{["Status","AP Name","Location Name","Location ID","Address","Latitude","Longitude","Affected Records","Missing Fields"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
                    <tbody>{analytics.invalidClients.map((r,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${C.dim}`,background:i%2===0?"transparent":"#0a1018"}}>
                        <td style={{padding:"8px 12px"}}>❌</td>
                        <td style={{padding:"8px 12px",color:C.text,whiteSpace:"nowrap"}}>{r.ap_name}</td>
                        <td style={{padding:"8px 12px",color:r.location_name==="—"?C.accent5:C.muted,whiteSpace:"nowrap"}}>{r.location_name}</td>
                        <td style={{padding:"8px 12px",color:r.location_id==="—"?C.accent5:C.muted}}>{r.location_id}</td>
                        <td style={{padding:"8px 12px",color:r.location_address==="—"?C.accent5:C.muted,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.location_address}</td>
                        <td style={{padding:"8px 12px",color:r.location_latitude==="—"?C.accent5:C.muted}}>{r.location_latitude}</td>
                        <td style={{padding:"8px 12px",color:r.location_longitude==="—"?C.accent5:C.muted}}>{r.location_longitude}</td>
                        <td style={{padding:"8px 12px",color:C.accent5,fontWeight:700}}>{fmtNum(r.count)}</td>
                        <td style={{padding:"8px 12px"}}><div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{Array.from(r.missing).map(f=><span key={f} style={{background:"#7f1d1d",color:"#fca5a5",padding:"2px 6px",borderRadius:4,fontSize:10,whiteSpace:"nowrap"}}>{f.replace("location_","")}</span>)}</div></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
            {analytics.invalidSessions.length>0&&(
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:20}}>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.text}}>Session Records with Missing Fields</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:2}}>{analytics.invalidSessions.length} APs affected — {fmtNum(analytics.totalInvalidSessions)} total records</div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{["Status","AP Name","Location Name","Location ID","Address","Latitude","Longitude","Affected Records","Missing Fields"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
                    <tbody>{analytics.invalidSessions.map((r,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${C.dim}`,background:i%2===0?"transparent":"#0a1018"}}>
                        <td style={{padding:"8px 12px"}}>❌</td>
                        <td style={{padding:"8px 12px",color:C.text,whiteSpace:"nowrap"}}>{r.ap_name}</td>
                        <td style={{padding:"8px 12px",color:r.location_name==="—"?C.accent5:C.muted}}>{r.location_name}</td>
                        <td style={{padding:"8px 12px",color:r.location_id==="—"?C.accent5:C.muted}}>{r.location_id}</td>
                        <td style={{padding:"8px 12px",color:r.location_address==="—"?C.accent5:C.muted,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.location_address}</td>
                        <td style={{padding:"8px 12px",color:r.location_latitude==="—"?C.accent5:C.muted}}>{r.location_latitude}</td>
                        <td style={{padding:"8px 12px",color:r.location_longitude==="—"?C.accent5:C.muted}}>{r.location_longitude}</td>
                        <td style={{padding:"8px 12px",color:C.accent5,fontWeight:700}}>{fmtNum(r.count)}</td>
                        <td style={{padding:"8px 12px"}}><div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{Array.from(r.missing).map(f=><span key={f} style={{background:"#7f1d1d",color:"#fca5a5",padding:"2px 6px",borderRadius:4,fontSize:10,whiteSpace:"nowrap"}}>{f.replace("location_","")}</span>)}</div></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
            {analytics.invalidClients.length===0&&analytics.invalidSessions.length===0&&(
              <div style={{background:C.card,border:`1px solid #10b981`,borderRadius:10,padding:40,textAlign:"center"}}>
                <div style={{fontSize:40,marginBottom:12}}>✅</div>
                <div style={{fontSize:16,fontWeight:600,color:C.accent3}}>All location fields are present and valid!</div>
                <div style={{fontSize:13,color:C.muted,marginTop:6}}>No missing fields detected across all client and session records.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
